//src/components/gro/SurveyCutsTable.tsx
import { ChevronDown, HelpCircle } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import type { SurveyResultCut } from "@/types/gro";

type Props = {
  items?: SurveyResultCut[] | null;
  maxScore?: number;
};

type RiskPresentation = {
  text: string;
  className: string;
};

function safeNumber(value?: number | null) {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

function formatScore(value?: number | null) {
  return Number(safeNumber(value).toFixed(2)).toString();
}

function riskPresentation(level: SurveyResultCut["risk_level"]): RiskPresentation {
  if (level === "critical") {
    return {
      text: "Risco Crítico",
      className: "border-red-200 bg-red-50 text-red-700",
    };
  }

  if (level === "high") {
    return {
      text: "Risco Alto",
      className: "border-amber-200 bg-amber-50 text-amber-700",
    };
  }

  if (level === "moderate") {
    return {
      text: "Risco Moderado",
      className: "border-slate-200 bg-slate-100 text-slate-700",
    };
  }

  return {
    text: "Risco Baixo",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
}

function cutTypeLabel(type: SurveyResultCut["cut_type"]) {
  if (type === "unit") return "Unidade";
  if (type === "department") return "Departamento";
  if (type === "job_position") return "Cargo";
  return "Grupo";
}

function CutCard({ item, maxScore }: { item: SurveyResultCut; maxScore: number }) {
  const [open, setOpen] = useState(false);
  const averageScore = safeNumber(item.average_score);
  const pct = Math.min((averageScore / maxScore) * 100, 100);
  const cutRisk = riskPresentation(item.risk_level || "low");
  const dimensions = Array.isArray(item.dimensions) ? item.dimensions : [];

  const dimAvgCalc =
    dimensions.length > 0
      ? +(
          dimensions.reduce((sum, dimension) => sum + safeNumber(dimension.average_score), 0) /
          dimensions.length
        ).toFixed(2)
      : 0;

  return (
    <Card className="overflow-hidden">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-muted/30">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-semibold">{item.cut_name}</span>

                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {cutTypeLabel(item.cut_type)}
                </Badge>
              </div>

              <div className="mt-0.5 text-xs text-muted-foreground">
                {item.respondents_count} respondente(s) • {dimensions.length} dimensão(ões)
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <div className="hidden text-right sm:block">
                <div className="text-sm font-bold">{formatScore(averageScore)}</div>
                <div className="text-[10px] text-muted-foreground">média</div>
              </div>

              <Badge variant="outline" className={`shrink-0 ${cutRisk.className}`}>
                {cutRisk.text}
              </Badge>

              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  open ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-3 border-t px-4 pb-4 pt-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Média geral do corte</span>
                <span className="font-semibold text-foreground">
                  {formatScore(averageScore)} / {maxScore}
                </span>
              </div>

              <Progress value={pct} className="h-2" />
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {dimensions.map((dimension, index) => {
                const dimensionAverageScore = safeNumber(dimension.average_score);
                const dimPct = Math.min((dimensionAverageScore / maxScore) * 100, 100);
                const dimRisk = riskPresentation(dimension.risk_level || "low");

                return (
                  <div
                    key={`${item.id}-${dimension.dimension_id || index}`}
                    className="space-y-2 rounded-lg border p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium">
                        {dimension.dimension_name ||
                          dimension.dimension_code ||
                          "Sem dimensão"}
                      </div>

                      <Badge
                        variant="outline"
                        className={`shrink-0 text-[10px] ${dimRisk.className}`}
                      >
                        {dimRisk.text}
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Média</span>
                        <span className="font-semibold text-foreground">
                          {formatScore(dimensionAverageScore)}
                        </span>
                      </div>

                      <Progress value={dimPct} className="h-1.5" />
                    </div>

                    <div className="text-[11px] text-muted-foreground">
                      {dimension.responses_count} respostas
                    </div>
                  </div>
                );
              })}
            </div>

            {dimensions.length > 0 && (
              <div className="space-y-1 rounded-lg border bg-muted/30 px-4 py-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                  <HelpCircle className="h-3.5 w-3.5 text-primary" />
                  Como chegamos na média deste corte
                </div>

                <div className="text-xs text-muted-foreground">
                  <span>
                    ({dimensions.map((dimension) => formatScore(dimension.average_score)).join(" + ")}) /{" "}
                    {dimensions.length} ={" "}
                  </span>
                  <span className="font-semibold text-foreground">
                    {formatScore(dimAvgCalc)}
                  </span>
                </div>

                <div className="text-[11px] italic text-muted-foreground">
                  A média do corte é calculada pela média aritmética das médias de cada
                  dimensão.
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export function SurveyCutsTable({ items, maxScore = 5 }: Props) {
  const safeItems = Array.isArray(items) ? items : [];

  if (safeItems.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Nenhum corte analítico encontrado para os filtros atuais.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <HoverCard>
        <HoverCardTrigger asChild>
          <button className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
            <HelpCircle className="h-3.5 w-3.5" />
            Entenda os cortes analíticos
          </button>
        </HoverCardTrigger>

        <HoverCardContent className="w-[380px] text-xs" side="bottom" align="start">
          <p className="mb-2 text-sm font-semibold">O que são cortes analíticos?</p>

          <p className="text-muted-foreground">
            Cortes analíticos segmentam os resultados da pesquisa por unidade,
            departamento, cargo ou grupo de trabalhadores. Cada corte mostra a média
            por dimensão e a média geral do segmento, permitindo identificar áreas de
            atenção.
          </p>

          <div className="mt-2 text-muted-foreground">
            <p className="font-medium text-foreground">Faixas de risco exibidas:</p>
            <p className="mt-1">Risco Baixo, Risco Moderado, Risco Alto e Risco Crítico.</p>
          </div>

          <div className="mt-2 text-muted-foreground">
            <p className="font-medium text-foreground">Fórmula da média do corte:</p>
            <p className="mt-1">Soma das médias de cada dimensão ÷ número de dimensões</p>
          </div>
        </HoverCardContent>
      </HoverCard>

      {safeItems.map((item) => (
        <CutCard key={item.id} item={item} maxScore={maxScore} />
      ))}
    </div>
  );
}
